import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../api";
import { showAlert, showConfirm } from "../../utils/popup";
import Button from "../../components/common/Button";
import { TextInput, SelectInput, TextareaInput } from "../../components/common/form";
import DatePicker from "../../components/common/DatePicker";
import HolidayEmailPreviewModal from "../../components/admin/HolidayEmailPreviewModal";
import PageHeaderSection from "../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../components/layout/PageLoadingShell";
import { ddmmyyyyToIso, formatDate, toISODate, getDateFormatPlaceholder, todayIso } from "../../utils/date";
import PaginationFooter from "../../components/common/PaginationFooter";
import SortableTableHeader from "../../components/common/SortableTableHeader";
import useSortedClientPagination from "../../hooks/useSortedClientPagination";
import { holidaySortColumns } from "../../config/tableSortColumns";
import { branchDisplayName } from '../../utils/displayNames';
import { apiErrorMessage } from '../../utils/apiError';

function isHolidayPast(holiday) {
    const end = toISODate(holiday?.endDate);
    return !!end && end < todayIso();
}

export default function HolidayManagement() {
    const { t, i18n } = useTranslation();
    const [rows, setRows] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [err, setErr] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [form, setForm] = useState({ name: "", startDate: "", endDate: "", branchId: "", notes: "" });
    const [branchesLoadError, setBranchesLoadError] = useState(false);

    const branchMap = useMemo(() => {
        const map = {};
        for (const b of branches) {
            const id = b.id ?? b.branchId;
            if (id != null) map[id] = b.name || b.branchName || branchDisplayName(null, id, t);
        }
        return map;
    }, [branches]);

    const pagination = useSortedClientPagination(rows, { columns: holidaySortColumns });
    const pastCount = useMemo(() => rows.filter(isHolidayPast).length, [rows]);

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const res = await API.get("/admin/holidays");
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch {
            setErr(t('settings.holidays.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        API.get("/admin/branches")
            .then((res) => {
                setBranches(Array.isArray(res?.data) ? res.data : []);
                setBranchesLoadError(false);
            })
            .catch(() => {
                setBranches([]);
                setBranchesLoadError(true);
            });
    }, []);

    const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const buildPayload = () => ({
        name: form.name.trim(),
        startDate: ddmmyyyyToIso(form.startDate),
        endDate: ddmmyyyyToIso(form.endDate),
        branchId: form.branchId ? Number(form.branchId) : null,
        notes: form.notes.trim() || null,
        uiLanguage: String(i18n.language || 'en').slice(0, 2),
    });

    const validateForm = () => {
        if (!form.name.trim()) {
            showAlert(t('settings.holidays.nameRequired'));
            return false;
        }
        if (!form.startDate || !form.endDate) {
            showAlert(t('settings.holidays.datesRequired'));
            return false;
        }
        const startIso = ddmmyyyyToIso(form.startDate);
        const endIso = ddmmyyyyToIso(form.endDate);
        if (!startIso || !endIso) {
            showAlert(t('settings.holidays.validDatesFormat', { format: getDateFormatPlaceholder() }));
            return false;
        }
        if (endIso < startIso) {
            showAlert(t('settings.holidays.endDateInvalid'));
            return false;
        }
        return true;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setPreviewLoading(true);
        try {
            const { data } = await API.post("/admin/holidays/notification-preview", buildPayload());
            setPreview(data);
            setPreviewOpen(true);
        } catch (ex) {
            showAlert(apiErrorMessage(ex, t('settings.holidays.previewFailed')));
        } finally {
            setPreviewLoading(false);
        }
    };

    const saveHoliday = async (sendNotifications) => {
        setSaving(true);
        try {
            const { data } = await API.post("/admin/holidays", {
                ...buildPayload(),
                sendNotifications,
            });
            setForm({ name: "", startDate: "", endDate: "", branchId: "", notes: "" });
            setPreviewOpen(false);
            setPreview(null);
            load();
            if (sendNotifications && data?.notificationsQueued) {
                await showAlert({
                    title: t('settings.holidays.saveSuccessTitle'),
                    message: t('settings.holidays.saveSuccessWithEmails'),
                    variant: "success",
                });
            } else {
                await showAlert({
                    title: t('settings.holidays.saveSuccessTitle'),
                    message: t('settings.holidays.saveSuccessWithoutEmails'),
                    variant: "success",
                });
            }
        } catch (ex) {
            showAlert(apiErrorMessage(ex, t('settings.holidays.saveHolidayFailed')));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (holiday) => {
        const past = isHolidayPast(holiday);
        const name = holiday.name || t('settings.holidays.thisHoliday');
        const confirmed = await showConfirm({
            title: past ? t('settings.holidays.deletePastTitle') : t('settings.holidays.deleteTitle'),
            message: past
                ? t('settings.holidays.deletePastMessage', { name })
                : t('settings.holidays.deleteMessage', { name }),
        });
        if (!confirmed) return;
        try {
            await API.delete(`/admin/holidays/${holiday.id}`);
            load();
        } catch (ex) {
            showAlert(apiErrorMessage(ex, t('settings.holidays.deleteFailed')));
        }
    };

    const startIso = ddmmyyyyToIso(form.startDate);
    const endIso = ddmmyyyyToIso(form.endDate);
    const dateRangeInvalid = startIso && endIso && endIso < startIso;

    if (loading && rows.length === 0) {
        return (
            <PageLoadingShell
                variant="page-shell"
                title={t('settings.holidays.title')}
                subtitle={t('settings.holidays.loadingSubtitle')}
            />
        );
    }

    return (
        <div className="list-page">
            <PageHeaderSection
                eyebrow={t('nav.system')}
                title={t('settings.holidays.title')}
                subtitle={t('settings.holidays.subtitle')}
            />

            <div className="space-y-6">
                {err && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {err}
                    </div>
                )}

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t('settings.holidays.stepOne')}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t('settings.holidays.addNew')}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t('settings.holidays.addNewDesc')}
                        </p>
                    </div>

                    <form onSubmit={submit} className="px-5 py-5 sm:px-6">
                        <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
                            <div className="space-y-5">
                                <TextInput
                                    label={t('settings.holidays.holidayName')}
                                    name="name"
                                    required
                                    value={form.name}
                                    onChange={(e) => setField("name", e.target.value)}
                                    placeholder={t('settings.holidays.namePlaceholder')}
                                />

                                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    <div className="form-group mb-0">
                                        <label htmlFor="holiday-start-date" className="form-label">
                                            {t('settings.holidays.startDate')} <span className="text-danger-600">*</span>
                                        </label>
                                        <DatePicker
                                            value={form.startDate}
                                            onChange={(value) => setField("startDate", value)}
                                            className="block w-full"
                                        />
                                    </div>
                                    <div className="form-group mb-0">
                                        <label htmlFor="holiday-end-date" className="form-label">
                                            {t('settings.holidays.endDate')} <span className="text-danger-600">*</span>
                                        </label>
                                        <DatePicker
                                            value={form.endDate}
                                            onChange={(value) => setField("endDate", value)}
                                            className="block w-full"
                                        />
                                        {dateRangeInvalid && (
                                            <p className="form-error">{t('settings.holidays.endDateInvalid')}</p>
                                        )}
                                    </div>
                                </div>

                                {branchesLoadError && (
                                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status">
                                        {t('common.optionsLoadFailed')}
                                    </div>
                                )}
                                <SelectInput
                                    id="holiday-branch"
                                    label={t('settings.holidays.branch')}
                                    value={form.branchId}
                                    onChange={(e) => setField("branchId", e.target.value)}
                                    helper={t('settings.holidays.branchHelper')}
                                    disabled={branchesLoadError}
                                >
                                    <option value="">{t('settings.holidays.allBranches')}</option>
                                    {branches.map((b) => {
                                        const id = b.id ?? b.branchId;
                                        return (
                                            <option key={id} value={id}>
                                                {b.name || b.branchName || branchDisplayName(null, id, t)}
                                            </option>
                                        );
                                    })}
                                </SelectInput>
                            </div>

                            <TextareaInput
                                id="holiday-notes"
                                label={t('settings.holidays.notes')}
                                className="min-h-[11.5rem] resize-y lg:min-h-[14.5rem]"
                                value={form.notes}
                                onChange={(e) => setField("notes", e.target.value)}
                                placeholder={t('settings.holidays.notesPlaceholder')}
                                rows={6}
                            />
                        </div>

                        <div className="mt-5 flex justify-end border-t border-slate-100 pt-4">
                            <Button
                                type="submit"
                                variant="page-primary"
                                loading={previewLoading || saving}
                                disabled={previewLoading || saving || dateRangeInvalid}
                            >
                                {t('settings.holidays.addHoliday')}
                            </Button>
                        </div>
                    </form>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                                    {t('settings.holidays.stepTwo')}
                                </p>
                                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                                    {t('settings.holidays.configured')}
                                </h2>
                            </div>
                            {!loading && rows.length > 0 && (
                                <div className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                                    <p className="text-xs font-medium text-slate-500">{t('settings.holidays.total', { count: rows.length })}</p>
                                    {pastCount > 0 && (
                                        <p className="text-xs text-slate-500">
                                            {t('settings.holidays.upcomingPast', {
                                                upcoming: rows.length - pastCount,
                                                past: pastCount,
                                            })}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500">
                                <tr>
                                    <SortableTableHeader label={t('settings.holidays.holidayName')} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('settings.holidays.colFrom')} columnKey="from" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('settings.holidays.colTo')} columnKey="to" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('settings.holidays.colBranch')} columnKey="branch" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                                            {t('settings.holidays.loadingSubtitle')}
                                        </td>
                                    </tr>
                                ) : err ? (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-10 text-center text-red-700">
                                            {err}
                                        </td>
                                    </tr>
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                                            {t('settings.holidays.noHolidays')}
                                        </td>
                                    </tr>
                                ) : pagination.pageItems.map((h) => {
                                    const past = isHolidayPast(h);
                                    return (
                                    <tr key={h.id} className={`text-slate-700 ${past ? "bg-slate-50/80" : ""}`}>
                                        <td className="px-4 py-2.5 font-medium text-slate-900">
                                            <span className="inline-flex flex-wrap items-center gap-2">
                                                {h.name}
                                                {past && (
                                                    <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                                        {t('settings.holidays.past')}
                                                    </span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5">{formatDate(h.startDate)}</td>
                                        <td className="px-4 py-2.5">{formatDate(h.endDate)}</td>
                                        <td className="px-4 py-2.5">
                                            {h.branchId ? (
                                                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                                                    {branchMap[h.branchId] || `#${h.branchId}`}
                                                </span>
                                            ) : (
                                                <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                                                    {t('settings.holidays.allBranchesBadge')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <button
                                                type="button"
                                                onClick={() => remove(h)}
                                                className="text-sm font-medium text-red-600 hover:text-red-800"
                                                title={past ? t('settings.holidays.deletePastHint') : t('settings.holidays.deleteHint')}
                                            >
                                                {t('settings.holidays.delete')}
                                            </button>
                                        </td>
                                    </tr>
                                );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {!loading && pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </section>
            </div>

            <HolidayEmailPreviewModal
                open={previewOpen}
                preview={preview}
                saving={saving}
                onClose={() => {
                    if (saving) return;
                    setPreviewOpen(false);
                }}
                onConfirm={saveHoliday}
            />
        </div>
    );
}
